import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import { agentChannels, agentTones, type AgentRecord, type AgentTemplate } from "@/mocks/aiagents";

/* Боковая панель «Новый AI-агент»: имя, цель, инструкции, тон общения и каналы.
   Открывается из шаблона, с нуля или из карточки готового агента. */

const FIELD =
  "w-full box-border rounded-btn border border-border bg-background px-2.5 text-[12.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

type Props = {
  mode: "new" | "edit";
  template?: AgentTemplate;
  agent?: AgentRecord;
  onClose: () => void;
  onSave: (record: AgentRecord) => void;
};

export default function AgentPanel({ mode, template, agent, onClose, onSave }: Props) {
  const [name, setName] = useState(agent?.name ?? (template ? `${template.name}` : ""));
  const [goal, setGoal] = useState(agent?.goal ?? template?.goal ?? "");
  const [instructions, setInstructions] = useState(agent?.instructions ?? template?.instructions ?? "");
  const [tone, setTone] = useState(agent?.tone ?? template?.tone ?? agentTones[0]);
  const [channels, setChannels] = useState<string[]>(agent?.channels ?? template?.channels ?? ["telegram"]);
  const [handoff, setHandoff] = useState(agent?.handoff ?? true);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const ready = name.trim().length >= 2;

  const toggleChannel = (id: string) => {
    setChannels((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  const save = () => {
    setTouched(true);
    if (!ready) return;
    onSave({
      id: agent?.id ?? `agent-${Date.now()}`,
      name: name.trim(),
      emoji: agent?.emoji ?? template?.emoji ?? "🤖",
      role: agent?.role ?? template?.role ?? t("Свой агент"),
      template: agent?.template ?? template?.id ?? "scratch",
      goal: goal.trim(),
      instructions: instructions.trim(),
      tone,
      channels,
      enabled: agent?.enabled ?? true,
      handoff,
      dialogs: agent?.dialogs ?? 0,
      resolved: agent?.resolved ?? 0,
    });
  };

  return (
    <div className="absolute inset-0 z-[60] flex justify-end">
      <button type="button" onClick={onClose} aria-label={t("Закрыть панель")} className="absolute inset-0 cursor-pointer bg-black/45 backdrop-blur-[1px]" />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-panel-title"
        className="relative flex h-full w-full max-w-[23.5rem] flex-col border-s border-border bg-card shadow-lift"
      >
        <div className="flex items-center gap-2 border-b border-border px-3.5 py-3">
          <span className="grid size-7 flex-none place-items-center rounded-btn bg-primary/10 text-[13px] leading-none" aria-hidden>
            {agent?.emoji ?? template?.emoji ?? "🤖"}
          </span>
          <div className="min-w-0 flex-1">
            <h4 id="agent-panel-title" className="truncate text-[13.5px] font-semibold leading-snug">
              {mode === "new" ? t("Новый AI-агент") : t("Настройки агента")}
            </h4>
            {template ? <p className="truncate text-[10.5px] text-muted-foreground">{t("Шаблон «{name}»", { name: template.name })}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть панель")}
            className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3">
          <label className="block">
            <span className="text-[11.5px] font-semibold">
              {t("Имя агента")} <span className="text-primary">*</span>
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder={t("Например, Аня — приём обращений")}
              autoFocus
              aria-invalid={touched && !ready}
              className={cn(FIELD, "mt-1 h-9", touched && !ready && "border-red-500/60 focus:border-red-500 focus:ring-red-500/20")}
            />
            {touched && !ready ? <span className="mt-1 block text-[10.5px] text-red-600">{t("Введите имя — от 2 символов")}</span> : null}
          </label>

          <label className="mt-3 block">
            <span className="text-[11.5px] font-semibold">{t("Цель агента")}</span>
            <textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value.slice(0, 400))}
              rows={2}
              placeholder={t("Что агент должен сделать в диалоге")}
              className={cn(FIELD, "mt-1 min-h-[58px] resize-y py-2 leading-relaxed")}
            />
          </label>

          <label className="mt-3 block">
            <span className="text-[11.5px] font-semibold">{t("Инструкции и правила")}</span>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value.slice(0, 600))}
              rows={4}
              placeholder={t("Как здороваться, что уточнять, когда передавать человеку")}
              className={cn(FIELD, "mt-1 min-h-[92px] resize-y py-2 leading-relaxed")}
            />
            <span className="mt-1 block text-[10px] text-muted-foreground">{instructions.length}/600 · {t("агент отвечает по источникам знаний")}</span>
          </label>

          <div className="mt-3">
            <span className="text-[11.5px] font-semibold">{t("Тон общения")}</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {agentTones.map((t) => {
                const active = tone === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTone(t)}
                    aria-pressed={active}
                    className={cn(
                      "cursor-pointer whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
                      active ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground",
                    )}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-3">
            <span className="text-[11.5px] font-semibold">{t("Каналы")}</span>
            <div className="mt-1 grid grid-cols-2 gap-1.5">
              {agentChannels.map((c) => {
                const active = channels.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleChannel(c.id)}
                    aria-pressed={active}
                    className={cn(
                      "flex cursor-pointer items-center gap-1.5 rounded-btn border px-2 py-1.5 text-[11.5px] font-medium transition",
                      active ? "border-primary/50 bg-primary/[.07] text-foreground" : "border-border text-muted-foreground hover:border-primary/40",
                    )}
                  >
                    <i className={cn(c.icon, "text-[13px] leading-none", active ? "text-primary" : "")} aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-start">{c.label}</span>
                    {active ? <Check className="size-3 flex-none text-primary" strokeWidth={3} aria-hidden /> : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-3 flex items-start justify-between gap-3 rounded-btn border border-border bg-background/50 p-2.5">
            <div className="min-w-0">
              <p className="text-[11.5px] font-semibold leading-snug">{t("Передавать человеку")}</p>
              <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">
                {t("Если клиент просит сотрудника или ответа нет в источниках знаний.")}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={handoff}
              aria-label={t("Передавать человеку")}
              onClick={() => setHandoff((v) => !v)}
              className={cn("relative mt-0.5 h-5 w-9 flex-none cursor-pointer rounded-full border transition", handoff ? "border-primary bg-primary" : "border-border bg-foreground/10")}
            >
              <span className={cn("absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all", handoff ? "start-[1.15rem]" : "start-[0.15rem]")} aria-hidden />
            </button>
          </div>

          <p className="mt-3 rounded-btn border border-border bg-background/50 p-2.5 text-[10.5px] leading-relaxed text-muted-foreground">
            {t("Агент отвечает только по источникам знаний и шаблонам рабочего пространства. Проверьте инструкции перед включением — он начнёт отвечать клиентам сразу.")}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-3.5 py-2.5">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn px-3 text-[12.5px] font-semibold text-muted-foreground transition hover:text-foreground"
          >
            {t("Отмена")}
          </button>
          <button
            type="button"
            onClick={save}
            aria-disabled={!ready}
            className={cn(
              "inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-btn px-4 text-[12.5px] font-semibold transition",
              ready ? "cursor-pointer bg-primary text-primary-foreground hover:brightness-110" : "cursor-not-allowed bg-foreground/10 text-muted-foreground",
            )}
          >
            {mode === "new" ? t("Создать агента") : t("Сохранить")}
          </button>
        </div>
      </div>
    </div>
  );
}